import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, User, LogOut, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = () => {
  const location = useLocation();
  const { cartItemCount, setIsCartOpen } = useCart();
  const { user, isAdmin, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/store', label: 'Store' },
    { href: '/track-order', label: 'Track Order' },
    { href: '/about', label: 'About' },
  ];

  const isActive = (path) => location.pathname === path;

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-header" data-testid="header">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="font-serif text-2xl font-semibold tracking-tight text-[#0A0A0A] hover:opacity-70"
            data-testid="logo"
          >
            <img src="/images/logos/by_logo.png" className="h-20" alt="Branding Yard" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm tracking-wide uppercase font-medium transition-opacity ${
                  isActive(link.href) 
                    ? 'text-[#0A0A0A]' 
                    : 'text-[#737373] hover:text-[#0A0A0A]'
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="hidden md:flex items-center gap-2 p-2 hover:bg-[#F5F5F5] transition-colors" data-testid="user-menu">
                    <div className="w-8 h-8 bg-[#0A0A0A] rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {user.email?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-none">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-[#0A0A0A]">{user.email}</p>
                    {isAdmin && (
                      <span className="text-xs text-[#C9A962]">Admin</span>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/account" className="cursor-pointer">
                      <User className="w-4 h-4 mr-2" />
                      My Account
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer">
                        <User className="w-4 h-4 mr-2" />
                        Admin Dashboard
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="rounded-none text-sm" data-testid="login-link">
                    Sign In
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button size="sm" className="rounded-none bg-[#0A0A0A] hover:bg-[#262626] text-sm" data-testid="signup-link">
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}
            
            {/* Cart Button */}
            <button
              onClick={() => setIsCartOpen(true)}
              className="relative p-2 hover:bg-[#F5F5F5] transition-colors"
              data-testid="cart-button"
            >
              <ShoppingBag className="w-5 h-5 text-[#0A0A0A]" />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#0A0A0A] text-white text-xs flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </button>

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <button className="p-2" data-testid="mobile-menu-trigger">
                  <Menu className="w-5 h-5 text-[#0A0A0A]" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:w-80 p-0">
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5]">
                    <span className="font-serif text-xl font-semibold">Menu</span>
                    <button onClick={() => setMobileMenuOpen(false)}>
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <nav className="flex flex-col p-6 gap-6">
                    {navLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`text-lg font-medium ${
                          isActive(link.href) ? 'text-[#0A0A0A]' : 'text-[#737373]'
                        }`}
                        data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                      >
                        {link.label}
                      </Link>
                    ))}
                    
                    <div className="border-t border-[#E5E5E5] pt-6">
                      {user ? (
                        <>
                          <p className="text-sm text-[#737373] mb-4">{user.email}</p>
                          {isAdmin && (
                            <Link
                              to="/admin"
                              onClick={() => setMobileMenuOpen(false)}
                              className="block text-lg font-medium text-[#737373] mb-4"
                            >
                              Admin Dashboard
                            </Link>
                          )}
                          <button
                            onClick={() => {
                              handleSignOut();
                              setMobileMenuOpen(false);
                            }}
                            className="text-lg font-medium text-red-600"
                          >
                            Sign Out
                          </button>
                        </>
                      ) : (
                        <div className="space-y-3">
                          <Link
                            to="/login"
                            onClick={() => setMobileMenuOpen(false)}
                            className="block text-lg font-medium text-[#0A0A0A]"
                          >
                            Sign In
                          </Link>
                          <Link
                            to="/signup"
                            onClick={() => setMobileMenuOpen(false)}
                            className="block text-lg font-medium text-[#C9A962]"
                          >
                            Create Account
                          </Link>
                        </div>
                      )}
                    </div>
                  </nav>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
